import styled from '@emotion/styled';
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import Fade from '../../animations/Fade';
import Grow from '../../animations/Grow';
import styles from '../../constants/styles';
import BodyEnd from '../BodyEnd';
import Box from '../Box';
import Flex from '../Flex';
import { TextProps } from '../Text';
import SmallBody from '../Text/SmallBody';

// Local Typings
type TooltipPlacement = 'bottom' | 'left' | 'right' | 'top';
interface Coords {
  bottom?: number;
  left?: number;
  right?: number;
  top?: number;
}
interface Props extends TooltipUIProps {
  alignment: TextProps['alignment'];
  noWrap?: boolean;
  tooltipLabel: ReactNode;
}
export interface TooltipUIProps {
  coords: Coords;
  tooltipPlacement: TooltipPlacement;
}
interface TooltipPositionProps extends TooltipUIProps {
  tooltipHeight: number;
  tooltipWidth: number;
}

// Local Variables
const arrowDimension = 5;
function getTooltipPosition({
  coords,
  tooltipHeight,
  tooltipPlacement,
  tooltipWidth,
}: TooltipPositionProps): Coords & { isInXView: boolean; isInYView: boolean } {
  const height = coords.top !== undefined && coords.bottom !== undefined ? (coords.bottom - coords.top) : undefined;
  const width = coords.left !== undefined && coords.right !== undefined ? (coords.right - coords.left) : undefined;
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  let top = height && coords.top !== undefined
    ? coords.top + height / 2
    : coords.top;
  let left = width && coords.left
    ? coords.left + width / 2
    : coords.left;

  if (tooltipPlacement === 'bottom') {
    top = coords.bottom;
  } else if (tooltipPlacement === 'top') {
    top = coords.top;
  }

  if (tooltipPlacement === 'left') {
    left = coords.left;
  } else if (tooltipPlacement === 'right') {
    left = coords.left && width ? coords.left + width : left;
  }

  const isInYView = !!height && !!top && (height + tooltipHeight + top < windowHeight) && (top > tooltipHeight);

  let isInXView = true;

  if (tooltipPlacement === 'left') {
    isInXView = !!left && left - tooltipWidth > 0;
  } else if (tooltipPlacement === 'right') {
    isInXView = !!width && !!left && (left + tooltipWidth + arrowDimension < windowWidth);
  }

  return {
    isInXView,
    isInYView,
    left,
    top,
  };
}
function getTransform(tooltipPlacement: TooltipPlacement): string {
  switch (tooltipPlacement) {
    case 'top':
      return 'translate(-50%, -100%)';
    case 'bottom':
      return 'translateX(-50%)';
    case 'left':
      return 'translate(-100%, -50%)';
    case 'right':
      return 'translateY(-50%)';
  }
}
function getRotate(tooltipPlacement: TooltipPlacement): string {
  switch (tooltipPlacement) {
    case 'top':
      return 'rotate(180deg)';
    case 'bottom':
      return 'rotate(0deg)';
    case 'left':
      return 'rotate(90deg) translateY(3px)';
    case 'right':
      return 'rotate(270deg) translateY(3px)';
  }
}
const Absolute = styled.div<{
  left?: number;
  top?: number;
  transform?: string;
}>(props => ({
  left: props.left,
  padding: `${styles.padding.xs} ${styles.padding.none}`,
  position: 'fixed',
  top: props.top,
  transform: props.transform,
  zIndex: styles.zIndex.modal + 1,
}));
const Arrow = styled.div<{ tooltipPlacement: TooltipPlacement }>(props => ({
  borderBottom: `${arrowDimension + 1}px solid ${props.theme.semanticColors.grayscale_900}`,
  borderLeft: `${arrowDimension + 1}px solid transparent`,
  borderRight: `${arrowDimension + 1}px solid transparent`,
  height: 0,
  transform: `${getRotate(props.tooltipPlacement)} translateY(1px)`,
  width: 0,
}));
const InnerArrow = styled.div({
  height: 0,
  left: -arrowDimension,
  position: 'relative',
  top: 1,
  width: 0,
});
const TooltipWrapper = styled(Box)(props => ({
  backgroundColor: props.theme.semanticColors.grayscale_900,
  borderRadius: styles.borderRadius.sm,
  color: props.theme.semanticColors.text_inverted,
  maxWidth: 530,
  padding: `${styles.padding.xs} ${styles.padding.sm}`,
}));

// Component Definition
const TooltipUI: FC<Props> = props => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [refHeight, setRefHeight] = useState(0);
  const [refWidth, setRefWidth] = useState(0);
  const [tooltipPlacement, setTooltipPlacement] = useState(props.tooltipPlacement);
  const [hasPlacementChanged, setHasPlacementChanged] = useState(false);

  const isVertical = tooltipPlacement === 'bottom' || tooltipPlacement === 'top';
  const isReversed = tooltipPlacement === 'top' || tooltipPlacement === 'left';

  const {
    isInXView,
    isInYView,
    left,
    top,
  } = getTooltipPosition({
    coords: props.coords,
    tooltipHeight: refHeight,
    tooltipPlacement,
    tooltipWidth: refWidth,
  });

  const transform = getTransform(tooltipPlacement);

  const flipX = useCallback(() => {
    if (tooltipPlacement === 'left') {
      setTooltipPlacement('right');
    } else {
      setTooltipPlacement('left');
    }
    setHasPlacementChanged(true);
  },
  [tooltipPlacement]);

  const flipY = useCallback(() => {
    if (tooltipPlacement === 'top') {
      setTooltipPlacement('bottom');
    } else {
      setTooltipPlacement('top');
    }
    setHasPlacementChanged(true);
  },
  [tooltipPlacement]);

  useEffect(() => {
    if (wrapperRef.current) {
      const extraY = isVertical ? arrowDimension : 0;
      const extraX = !isVertical ? arrowDimension : 0;
      setRefHeight(extraY + wrapperRef.current.clientHeight);
      setRefWidth(extraX + wrapperRef.current.clientWidth);
    }
  },
  [isVertical]);

  useEffect(() => {
    if (!isInYView && isVertical && !hasPlacementChanged) {
      flipY();
    } else if (!isInXView && !isVertical && !hasPlacementChanged) {
      flipX();
    }
  },
  [
    isInYView,
    isInXView,
    hasPlacementChanged,
    flipX,
    flipY,
    isVertical,
  ]);

  return (
    <BodyEnd>
      <Absolute
        className="tooltip"
        left={left}
        ref={wrapperRef}
        top={top}
        transform={transform}
      >
        <Grow transitionSpeedInMs={150}>
          <Fade
            isIn
            transitionSpeedInMs={300}
          >
            <Flex
              flexDirection={isVertical
                ? (
                  isReversed ? 'column-reverse' : 'column'
                )
                : (
                  isReversed ? 'row-reverse' : 'row'
                )}
            >
              <Flex justifyContent="center">
                <Arrow tooltipPlacement={tooltipPlacement}>
                  <InnerArrow />
                </Arrow>
              </Flex>
              <TooltipWrapper testId="tooltip-wrapper">
                <SmallBody
                  alignment={props.alignment}
                  as={typeof props.tooltipLabel === 'object' ? 'span' : 'p'}
                  fontColor="inherit"
                  margin={0}
                  noWrap={props.noWrap}
                >
                  {props.tooltipLabel}
                </SmallBody>
              </TooltipWrapper>
            </Flex>
          </Fade>
        </Grow>
      </Absolute>
    </BodyEnd>
  );
};

export default TooltipUI;
